import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';

import { Money, DateTime } from '../../../components/Formatters';
import { Table, Thead, Tbody, Tr, LinkTr, Th, Td } from '../../../components/Table/Fake';
import { ListGap } from '../../../components/Paging';
import TransactionStatus from '../../components/TransactionStatus';

import { getCurrentEvent, getCustomerName, getProductName, isTransaction } from '../../transactionHelpers';
import { getListKey } from '../../../helpers/paging';
import { TransactionOrGap } from '../types';
import LineBreaks from '../../../components/Formatters/LineBreaks';

interface RowProps extends WithTranslation {
    row: TransactionOrGap;
    accountId: string;
    fillGap: (id: string) => void;
}

const Row = (
    {
        row,
        accountId,
        fillGap,
        t,
    }: RowProps
) => {
    if (isTransaction(row)) {
        const status = row.status || getCurrentEvent(row).transaction_status;
        return (
            <LinkTr
                key={row.id}
                to={`/${accountId}/payments/transactions/${row.id}`}
                className="stop-borders-before-edges"
            >
                <Td className="edge" />
                <Td>
                    <DateTime isoString={row.created_at || ''} boldTime />
                </Td>
                <Td className="align-right">
                    <Money amount={row.amount} currency={row.currency} boldCurrency />
                </Td>
                <Td>
                    {row && <TransactionStatus transaction={row} />}
                </Td>
                <Td className="collapse-1200 ellipsis">{t(`payments.settlement_statuses.${row.settlement_status}`)}</Td>
                <Td className="collapse-768 ellipsis">{t(getProductName(row))}</Td>
                <Td className="collapse-1200 ellipsis">
                    <LineBreaks lines={[row.merchant_reference, row.merchant_reference_2].filter(x => x)} />
                </Td>
                <Td className="collapse-1200 ellipsis">{row.store?.id ?? ''}</Td>
                <Td className="collapse-1200 ellipsis">{getCustomerName(row)}</Td>
                <Td className="edge" />
            </LinkTr>
        );
    } else {
        return <ListGap fillGap={fillGap} gap={row} label={t('payments.transactions.load_gap')} />;
    }
};

const TranslatedRow = withTranslation()(Row);

interface StatusProps {
    status: string;
}

export interface TransactionTableProps extends WithTranslation {
    transactions: TransactionOrGap[];
    accountId: string;
    fillGap: (id: string) => void;
}

const TransactionTable = (
    {
        transactions,
        accountId,
        fillGap,
        t,
    }: TransactionTableProps
) => {
    return (
        <Table className="no-wrap">
            <Thead>
                <Tr>
                    <Th className="edge" />
                    <Th className="first-child">{t('payments.fields.date')}</Th>
                    <Th>{t('payments.fields.amount')}</Th>
                    <Th>{t('payments.fields.status')}</Th>
                    <Th className="collapse-1200">{t('payments.fields.payout')}</Th>
                    <Th className="collapse-768">{t('payments.fields.type')}</Th>
                    <Th className="collapse-1200">{t('payments.fields.merchant_reference')}</Th>
                    <Th className="collapse-1200">{t('payments.fields.store')}</Th>
                    <Th className="last-child collapse-1200">{t('payments.fields.customer')}</Th>
                    <Th className="edge" />
                </Tr>
            </Thead>
            {transactions.length > 0 && (
                <Tbody>
                    {transactions.map((tx) => (
                        <TranslatedRow key={getListKey(tx)} row={tx} accountId={accountId} fillGap={fillGap} />
                    ))}
                </Tbody>
            )}
        </Table>
    );
};

export default withTranslation()(TransactionTable);
